import * as React from 'react';
import { connect } from 'react-redux';
import { Col, Row } from 'reactstrap';
import { apiRequest } from '../../APIClient';
import { APIError } from '../../APIError';
import { dashboardSelector } from '../../reducers/dashboard';
import { sessionSelector } from '../../reducers/session';
import { Dispatch, PermissionEnum, SensorEntry } from '../../types';
import Viewport from '../../widgets/Viewport';
import './Dashboard.scss';
import IncidentScore from './IncidentScore';
import InvestigationScore from './InvestigationScore';
import SecurityInvestigations, {
  SecurityInvestigationsEntry,
} from './SecurityInvestigations';
import { NumericSecurityScore } from './SecurityScore';
import SensorScore from './SensorScore';
import TopCountriesMap, { TopCountriesEntry } from './TopCountriesMap';
import AgentSummary from './agent/AgentSummary';
import { memoizedSelectRolesAndServices } from '../../selectors';
import { RolesAndServices } from '../../selectors/selectRolesAndServices';
import { hasPermissions } from '../../constants';
import { PageTitle } from '../../widgets/PageTitle';

type OwnProps = {};

export type StoreProps = {
  countryFlowsError: APIError | null;
  incidentsError: APIError | null;
  investigations: Array<SecurityInvestigationsEntry>;
  loadingCountryFlows: boolean;
  loadingIncidents: boolean;
  loadingSummary: boolean;
  securityScore: string;
  numericSecurityScore: number | null;
  rolesAndServices: RolesAndServices;
  sensors: Array<SensorEntry>;
  ticketedInvestigations: number | null;
  topCountries: Array<TopCountriesEntry>;
  totalInvestigations: number | null;
};

export type DispatchProps = {
  fetchDashboardData: () => void;
};

export type Props = OwnProps & StoreProps & DispatchProps;

type SensorStats = {
  installed: number;
  unhealthyInstalled: number;
  hasSensors: boolean;
  hasScanners: boolean;
};

class DashboardView extends React.Component<Props> {
  componentDidMount() {
    this.props.fetchDashboardData();
  }

  render() {
    const {
      countryFlowsError,
      incidentsError,
      investigations,
      loadingCountryFlows,
      loadingIncidents,
      loadingSummary,
      rolesAndServices: { roles, services },
      sensors,
      ticketedInvestigations,
      topCountries,
      totalInvestigations,
      numericSecurityScore,
    } = this.props;
    const sensorStats = sensors.reduce(
      (result: SensorStats, sensor) => {
        return {
          installed: result.installed + (sensor.state === 'installed' ? 1 : 0),
          unhealthyInstalled:
            result.unhealthyInstalled +
            (sensor.health === 'DOWN' && sensor.state === 'installed' ? 1 : 0),
          hasSensors: result.hasSensors || sensor.type === 'physical',
          hasScanners: result.hasScanners || sensor.type === 'scanner',
        };
      },
      {
        installed: 0,
        unhealthyInstalled: 0,
        hasSensors: false,
        hasScanners: false,
      },
    );

    const securityScorePanel = (
      <NumericSecurityScore score={numericSecurityScore} />
    );

    return (
      <Viewport>
        <PageTitle title="Dashboard" />
        <Row>
          <Col xs={12} sm={6} md={4} className="psr-col">
            {securityScorePanel}
          </Col>
          <Col xs={12} sm={6} md={4} className="psr-col">
            <AgentSummary />
          </Col>
          <Col xs={12} sm={6} md={4} className="psr-col">
            <SensorScore
              sensorsWorking={
                sensorStats.installed - sensorStats.unhealthyInstalled
              }
              sensorsTotal={sensorStats.installed}
              hasSensors={sensorStats.hasSensors}
              hasScanners={sensorStats.hasScanners}
              isLoading={loadingSummary}
            />
          </Col>
          <Col sm={6} className="d-block d-md-none psr-col">
            <InvestigationScore
              totalInvestigations={totalInvestigations}
              isLoading={loadingIncidents}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={8} className="psr-col">
            <SecurityInvestigations
              investigations={investigations}
              isLoading={loadingIncidents}
              error={incidentsError}
            />
          </Col>
          <Col xs={12} md={4} className="mb-0 psr-col">
            <Row id="investigations_and_tickets">
              <Col sm={12} className="d-none d-md-block psr-col">
                <InvestigationScore
                  totalInvestigations={totalInvestigations}
                  isLoading={loadingIncidents}
                />
              </Col>
              <Col sm={12} className="psr-col">
                <IncidentScore
                  ticketedInvestigations={ticketedInvestigations}
                  isLoading={loadingIncidents}
                  className="incident-panel"
                />
              </Col>
            </Row>
          </Col>
        </Row>
        {hasPermissions(roles, services, PermissionEnum.VIEW_TOP_COUNTRIES) && (
          <Row>
            <Col className="psr-col">
              <TopCountriesMap
                topCountries={topCountries}
                isLoading={loadingCountryFlows}
                error={countryFlowsError}
              />
            </Col>
          </Row>
        )}
      </Viewport>
    );
  }
}

export default connect<StoreProps, DispatchProps, OwnProps>(
  (store): StoreProps => ({
    countryFlowsError: dashboardSelector.getCountryFlowsError(store),
    incidentsError: dashboardSelector.getIncidentsError(store),
    investigations: dashboardSelector.getInvestigations(store),
    loadingSummary: dashboardSelector.isLoadingSummary(store),
    loadingIncidents: dashboardSelector.isLoadingIncidents(store),
    loadingCountryFlows: dashboardSelector.isLoadingCountryFlows(store),
    securityScore: dashboardSelector.getSecurityScore(store),
    numericSecurityScore: sessionSelector.getSecurityReadinessScore(store),
    sensors: sessionSelector.getSensors(store),
    ticketedInvestigations:
      dashboardSelector.getTicketedInvestigationCount(store),
    topCountries: dashboardSelector.getTopCountries(store),
    totalInvestigations: dashboardSelector.getInvestigationCount(store),
    rolesAndServices: memoizedSelectRolesAndServices(store),
  }),
  (dispatch: Dispatch): DispatchProps => ({
    fetchDashboardData: () => dispatch(apiRequest.getDashboardData()),
  }),
)(DashboardView);
